// Guide/GuideOrder.tsx
import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Message} from 'Core/components/Message';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import {IGuide} from '../../Types/psychology/guide';
import {axios} from 'Auth/axiosConfig';
import {ICurrencyWithPrice, IPaymentSystem} from '../../Types/commerce/shop';
import PaymentSelector from "Order/PaymentSelector";
import {Typography} from '@mui/material';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import pprint from "../../Utils/pprint";
import moment from "moment";

const GuideOrder: React.FC = () => {
    const {isAuthenticated, user} = useContext(AuthContext) as AuthContextType;
    const {byResponse} = useErrorProcessing();
    const {productId} = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [product, setProduct] = useState<IGuide | null>(location.state?.product || null);
    const [loading, setLoading] = useState<boolean>(!location.state?.product && !!productId);
    const [error, setError] = useState<string | null>(null);
    const [orderCreating, setOrderCreating] = useState<boolean>(false);

    useEffect(() => {
        if (isAuthenticated !== null) {
            if (!isAuthenticated) {
                navigate('/auth', {state: {from: location}, replace: true});
                Message.info('Мы сохранили ваш выбор, войдите или зарегистрируйтесь для продолжения.', 1, 9000);
                return;
            }
        }
        const storedClientRequest = localStorage.getItem('clientRequest');

        if (storedClientRequest) {
            const clientRequest = JSON.parse(storedClientRequest);

            if (clientRequest.selected_slot) {
                clientRequest.selected_slot = moment(clientRequest.selected_slot);
            }

            if (!clientRequest.client_gender) {
                Message.error('Необходимо заполнить все данные перед оформлением заказа.');
                navigate('/questionnaire/general', {replace: true});
                return;
            } else {
                setLoading(false);
            }
        } else {
            Message.error('Необходимо заполнить данные перед оформлением заказа.');
            navigate('/questionnaire/general', {replace: true});
        }
    }, [isAuthenticated, navigate, location]);

    useEffect(() => {
        if (!product && productId) {
            const fetchProduct = async () => {
                setLoading(true);
                try {
                    const response = await axios.get<IGuide>(`/api/v1/guides/${productId}/`);
                    pprint('Guide for Order');
                    pprint(response.data);
                    setProduct(response.data);
                } catch (err: any) {
                    console.error('Ошибка при загрузке продукта:', err);
                    setError('Не удалось загрузить продукт.');
                } finally {
                    setLoading(false);
                }
            };
            fetchProduct();
        }
    }, [product, productId]);

    const createOrder = async (
        currency: ICurrencyWithPrice,
        paymentSystem: IPaymentSystem,
        promocode: number | null,
        email?: string
    ) => {
        if (!product) {
            Message.error('Продукт не найден.');
            return;
        }
        if (!currency) {
            Message.error('Выберите валюту.');
            return;
        }
        if (!paymentSystem) {
            Message.error('Выберите способ оплаты.');
            return;
        }
        setOrderCreating(true);
        try {
            const payload: any = {
                product: product.id,
                currency: currency.currency,
                payment_system: paymentSystem,
                promocode: promocode ? promocode : null,
            };

            if (email) {
                payload.email = email;
            }

            const response = await axios.post('/api/v1/orders/create/', payload);
            Message.success('Заказ успешно создан.');
            window.open(response.data, '_blank');
            navigate(`/orders`);
        } catch (error) {
            byResponse(error);
            setOrderCreating(false);
        }
    };

    if (loading) {
        return (
            <CircularProgress size={'150px'}/>
        );
    }

    if (error) {
        return (
            <Typography color="error" align="center" mt={4}>
                {error}
            </Typography>
        );
    }

    if (!product) {
        return (
            <Typography align="center" mt={4}>
                Продукт не найден.
            </Typography>
        );
    }

    return (
        <div className={'fc gap-2'} style={{maxWidth: '600px', margin: '0 auto', padding: '1rem'}}>
            <h2>{product.name}</h2>
            <p style={{whiteSpace: 'pre-wrap'}}>{product.description}</p>

            <PaymentSelector
                product={product}
                isLoading={orderCreating}
                onConfirm={(currency, paymentSystem, promocodeId, email) => {
                    createOrder(currency, paymentSystem, promocodeId, email);
                }}
            />
        </div>
    );
};

export default GuideOrder;